/**
 * Color helpers shared by the alignment visualizations.
 *
 * Per-matrix sequential scale stretched over the *actual* pair rate
 * range. Municipal council agreement rates typically cluster in
 * [0.80, 0.96], so a default 0-to-1 scale washes out: every cell
 * paints near the top of the ramp. Stretching across the matrix's
 * own min and max means a 0.82 cell reads visibly different from a
 * 0.94 cell.
 *
 * Colours come from the `--viridis-step-0..10` CSS variables in
 * `globals.css`, addressed by **agreement rank**: step 0 is the
 * lowest-rank rate, step 10 is the highest-rank rate. The variables
 * themselves swap palette positions between light and dark mode so
 * the polarity stays "more agreement = more attention-getting" in
 * both themes — dark purple weight on light paper, bright yellow on
 * the dark page. JS just picks the right step index; CSS handles the
 * theme flip.
 *
 * Viridis is perceptually uniform and colourblind-safe — equal rate
 * differences read as equal visual differences across overlapping
 * chord edges.
 */

import type { Pair } from "@/app/council/_lib/alignment-data";

const STOPS = 11;

/**
 * Map a normalized rank t ∈ [0, 1] to the matching `--viridis-step-N`
 * CSS variable reference, where N = round(t × 10). The CSS variables
 * carry the theme-aware palette, so the returned string is
 * `var(--viridis-step-N)` rather than a literal rgb() colour.
 */
function viridisVar(t: number): string {
  const clamped = Math.max(0, Math.min(1, t));
  const idx = Math.round(clamped * (STOPS - 1));
  return `var(--viridis-step-${idx})`;
}

export type AgreementScale = {
  min: number;
  median: number;
  max: number;
  /** Number of non-null pairs the scale was built from. */
  n: number;
  /** True when the scale degenerates (min == max). All cells render at MID. */
  degenerate: boolean;
  toColor: (rate: number | null) => string;
};

/**
 * Build a per-matrix colour scale. Returns a degenerate scale (the
 * viridis midpoint) when there are no contested pairs to stretch over
 * — falling back to a static 0..1 colourmap would mis-cue, so the
 * grid paints uniformly until variation exists.
 *
 * The scale stretches the matrix's [min, max] pair-rate range across
 * the full viridis ramp. `median` is still surfaced on the scale
 * object for the legend, but no longer anchors the gradient (the
 * sequential scale doesn't need a midpoint — it just runs from
 * darkest at min to brightest at max).
 */
export function buildAgreementScale(pairs: Pair[]): AgreementScale {
  const rates: number[] = [];
  for (const p of pairs) {
    if (p.agreement_rate !== null) rates.push(p.agreement_rate);
  }
  if (rates.length === 0) {
    return {
      min: 0,
      median: 0.5,
      max: 1,
      n: 0,
      degenerate: true,
      toColor: () => viridisVar(0.5),
    };
  }
  const sorted = [...rates].sort((a, b) => a - b);
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const mid = Math.floor(sorted.length / 2);
  const median =
    sorted.length % 2 === 0
      ? (sorted[mid - 1] + sorted[mid]) / 2
      : sorted[mid];
  const degenerate = max - min < 1e-6;
  return {
    min,
    median,
    max,
    n: rates.length,
    degenerate,
    toColor: degenerate
      ? () => viridisVar(0.5)
      : (rate) => {
          if (rate === null) return "rgb(190 184 174)";
          const r = Math.max(min, Math.min(max, rate));
          const t = (r - min) / (max - min);
          // No JS-side polarity inversion: the CSS variables in
          // light/dark mode already encode "more agreement = more
          // attention-getting" by swapping which palette position the
          // top-rank step references.
          return viridisVar(t);
        },
  };
}

/**
 * Bloc label → token-backed accent colour. Kept label-neutral: A/B/C/D
 * read as four distinct hues but carry no left/right valence.
 */
export const BLOC_COLOR: Record<string, string> = {
  A: "var(--denim)",
  B: "var(--ochre)",
  C: "var(--sage)",
  D: "var(--plum)",
};

export function blocColor(label: string | undefined): string {
  if (!label) return "var(--ink-3)";
  return BLOC_COLOR[label] ?? "var(--ink-3)";
}

export const SCALE_ENDPOINT_HEX = {
  /** Colour shown for the matrix's *lowest* observed rate. */
  low: "var(--viridis-step-0)",
  /** Colour shown for the median observed rate. */
  mid: "var(--viridis-step-5)",
  /** Colour shown for the matrix's *highest* observed rate. */
  high: "var(--viridis-step-10)",
};

/**
 * CSS linear-gradient string showing the agreement scale as a
 * continuous swatch. Runs left-to-right from the low-rate end of the
 * scale to the high-rate end. Because each stop is a CSS variable,
 * the swatch re-renders with the correct polarity for the active
 * theme — no JS work needed when the user flips dark/light mode.
 */
export const VIRIDIS_GRADIENT = (() => {
  const stops: string[] = [];
  for (let i = 0; i < STOPS; i++) {
    const pct = ((i / (STOPS - 1)) * 100).toFixed(1);
    stops.push(`var(--viridis-step-${i}) ${pct}%`);
  }
  return `linear-gradient(to right, ${stops.join(", ")})`;
})();
